/**
 * ScheduleFormDialog — create / edit an automated report schedule (Story 7.2).
 *
 * §4 centered MODAL Dialog (Radix DialogPrimitive + motion/react) cloning the
 * CreateShareLinkSheet scaffold EXACTLY: reset-on-OPEN, close blocked while a
 * mutation is pending, AnimatePresence, max-w-md content, motion-staggered
 * fields, §4.6 always-mounted (the parent never conditionally unmounts it for
 * a non-null schedule — it lingers via a last-state copy). NEVER a Sheet.
 *
 * Every picker reuses DialogSelect (LOCKED decision #4 — reuse, don't build):
 * Frequency; conditional Day-of-week (Mon..Sun → 0..6) for weekly/biweekly;
 * conditional Day-of-month (1..28 + "Last day"=-1) for monthly; Time (48
 * half-hour options, LOCAL to the selected timezone — Story 7.4); Timezone
 * (common IANA zones, default = the browser's zone → the client's timezone →
 * UTC); Template (lazy GET /api/report-templates, enabled: open). Recipients are
 * managed by the multi-recipient RecipientEditor (Story 7.8) — add/remove rows, one
 * Primary, optional names, suppressed-row badges, and a "Send me a copy" toggle.
 *
 * ── TIME / TIMEZONE SEMANTICS (Story 7.4 — Interpretation B, timezone authoritative) ──
 * The Time picker = the delivery time IN the selected timezone (local wall clock),
 * and that IS the stored time-of-day. The modal sends the picked "HH:MM" DIRECTLY
 * as `timeUtc` (no local→UTC conversion) for both the preview call and the
 * create/update payload; edit mode seeds the picker from the stored `timeUtc`
 * verbatim. The SERVER (computeNextRun) interprets that HH:MM as the local wall
 * clock in `timezone` and resolves the UTC `next_run_at`. This is DST-correct: a
 * 09:00-ET schedule fires at 09:00 ET on both sides of a DST boundary and never on
 * the wrong calendar day. The `timezone` field is authoritative (default = browser
 * zone → client's timezone → UTC). The preview shows the honest UTC-resolved
 * instants formatted back in the selected zone.
 *
 * ── LIVE PREVIEW (Story 7.4 — LOCKED decision #1) ────────────────────────────
 * The modal NEVER computes schedule math. A debounced useQuery POSTs the
 * (UTC-converted) cadence to /api/schedules/preview and renders "Next report:
 * <occ0>" + the next 3 dates, formatted via Intl in the selected tz. On success
 * it surfaces the full confirmation summary (frequency + timing, recipient,
 * template, "First delivery in X"). Inline validation mirrors the server Zod
 * (FREQUENCY_VALUES; dayOfWeek required iff weekly/biweekly; dayOfMonth 1..28 |
 * -1 required iff monthly; >= 1 valid recipient email); submit is disabled until
 * valid.
 */

import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Switch, Label } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { CalendarBlank, Clock } from "@phosphor-icons/react";
import {
  createSchedule,
  updateSchedule,
  previewSchedule,
  getScheduleRecipients,
  FREQUENCY_VALUES,
  LAST_DAY_OF_MONTH,
  type Frequency,
  type ScheduleProjection,
  type CreateScheduleInput,
  type UpdateScheduleInput,
  type ScheduleRecipientInput,
} from "../../lib/schedulesApi";
import { apiGet } from "../../lib/apiClient";
import { useAuthStore } from "../../stores/authStore";
import { DialogSelect, type DialogSelectOption } from "./DialogSelect";
import {
  RecipientEditor,
  validateRecipients,
  normalizeLoadedRecipients,
  type RecipientRow,
} from "./RecipientEditor";
import type { ToastType } from "../../hooks/useToast";

interface ScheduleFormDialogProps {
  clientId: string;
  /** The client's timezone (migration 0049 default "UTC") — defaults the picker. */
  clientTimezone?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When present the dialog is in EDIT mode and pre-fills from this schedule. */
  schedule?: ScheduleProjection | null;
  showToast: (type: ToastType, message: string) => void;
}

interface ReportTemplate {
  id: string;
  name?: string | null;
  title?: string | null;
}

/** Post-save confirmation summary (AC #3). */
interface ScheduleConfirmation {
  frequencyLabel: string;
  timing: string;
  recipient: string;
  templateName: string;
  nextRunLabel: string;
  firstDeliveryIn: string;
}

// ── Picker option tables (reused by every DialogSelect) ──────────────────────

const FREQUENCY_LABELS: Record<Frequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
};

const FREQUENCY_OPTIONS: DialogSelectOption[] = FREQUENCY_VALUES.map((f) => ({
  value: f,
  label: FREQUENCY_LABELS[f],
}));

// Display order Monday→Sunday; each `value` is the db encoding (0=Sun .. 6=Sat),
// which the server's dayOfWeekSchema validates (0..6) and computeNextRun consumes.
const DAY_OF_WEEK_OPTIONS: DialogSelectOption[] = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "0", label: "Sunday" },
];

const ordinal = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

// 1..28 fixed days + the LAST_DAY_OF_MONTH sentinel.
const DAY_OF_MONTH_OPTIONS: DialogSelectOption[] = [
  ...Array.from({ length: 28 }, (_, i) => {
    const day = i + 1;
    return { value: String(day), label: `${ordinal(day)} of the month` };
  }),
  { value: String(LAST_DAY_OF_MONTH), label: "Last day" },
];

// 48 half-hour "HH:MM" entries (00:00 .. 23:30), labelled in 12-hour clock. These
// are LOCAL times in the selected timezone (Story 7.4) — the picked value IS the
// stored time-of-day, sent verbatim as `timeUtc`; the SERVER interprets it in the
// selected tz (Interpretation B). No client-side local→UTC conversion.
const TIME_OPTIONS: DialogSelectOption[] = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  const value = `${String(h).padStart(2, "0")}:${m}`;
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { value, label: `${h12}:${m} ${period}` };
});

// A pragmatic set of common IANA zones (DialogSelect auto-searches > 8 options).
const TIMEZONE_VALUES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Toronto",
  "America/Sao_Paulo",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Athens",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
] as const;

const TIMEZONE_OPTIONS: DialogSelectOption[] = TIMEZONE_VALUES.map((tz) => ({
  value: tz,
  label: tz.replace(/_/g, " "),
}));

/**
 * Format a server-returned next-run epoch-ms in the schedule's tz with built-in
 * Intl (date-fns is NOT installed). Exported so SchedulesCard renders the same
 * way. Falls back to the local zone if the IANA name is somehow invalid.
 */
export function formatNextRun(nextRunAt: number, timezone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(new Date(nextRunAt));
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(nextRunAt));
  }
}

/**
 * The browser-detected zone → the client's stored timezone → "UTC" (Story 7.4
 * AC #2). Wrapped so a quirky environment (Intl returning "" or throwing) still
 * yields a usable default.
 */
export function detectDefaultTimezone(clientTimezone?: string | null): string {
  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browser) return browser;
  } catch {
    // fall through
  }
  return clientTimezone || "UTC";
}

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Human cadence timing for the confirmation summary, e.g. "Every Monday at 9:00 AM
 * EDT", "On the last day of the month at 6:00 AM UTC". The clock time is the LOCAL
 * pick; the tz abbreviation is derived via Intl for honesty.
 */
function cadenceTimingLabel(
  frequency: Frequency,
  dayOfWeek: number | null,
  dayOfMonth: number | null,
  localHHMM: string,
  timezone: string
): string {
  const [hStr, mStr] = localHHMM.split(":");
  const h = Number(hStr);
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const clock = `${h12}:${mStr} ${period}`;
  let tzAbbr = timezone;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    tzAbbr = parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    // keep the IANA name
  }
  const time = `at ${clock} ${tzAbbr}`;

  switch (frequency) {
    case "daily":
      return `Every day ${time}`;
    case "weekly":
      return `Every ${WEEKDAY_NAMES[dayOfWeek ?? 0]} ${time}`;
    case "biweekly":
      return `Every other ${WEEKDAY_NAMES[dayOfWeek ?? 0]} ${time}`;
    case "monthly":
      return dayOfMonth === LAST_DAY_OF_MONTH
        ? `On the last day of the month ${time}`
        : `On the ${ordinal(dayOfMonth ?? 1)} of the month ${time}`;
    default:
      return time;
  }
}

/**
 * Humanizes the gap between a server `nextRunAt` (epoch ms) and now into
 * "First delivery in X" (minutes / hours / days), for the post-save confirmation
 * summary (AC #3).
 */
export function formatFirstDeliveryIn(nextRunAt: number, now = Date.now()): string {
  const diffMs = Math.max(0, nextRunAt - now);
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) {
    return `First delivery in ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 48) {
    return `First delivery in ${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  const days = Math.round(diffMs / 86_400_000);
  return `First delivery in ${days} ${days === 1 ? "day" : "days"}`;
}

const FIELD_BORDER = "0.5px solid var(--hairline)";

function FieldShell({
  label,
  htmlFor,
  required,
  error,
  delay,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  delay: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
        {error && (
          <span
            className="text-sm text-destructive font-normal ml-1"
            role="alert"
          >
            {error}
          </span>
        )}
      </label>
      {children}
    </motion.div>
  );
}

export function ScheduleFormDialog({
  clientId,
  clientTimezone,
  open,
  onOpenChange,
  schedule,
  showToast,
}: ScheduleFormDialogProps) {
  const isEdit = Boolean(schedule);
  const queryClient = useQueryClient();
  const currentUserEmail = useAuthStore((s) => s.user?.email ?? "");

  // Auto-detect: browser zone → client's stored timezone → "UTC" (AC #2).
  const defaultTimezone = useMemo(
    () => detectDefaultTimezone(clientTimezone),
    [clientTimezone]
  );

  const [frequency, setFrequency] = useState<Frequency>("weekly");
  const [dayOfWeek, setDayOfWeek] = useState<string>("1"); // Monday
  const [dayOfMonth, setDayOfMonth] = useState<string>("1");
  // `localTime` is the picked LOCAL "HH:MM" in the selected timezone (Story 7.4);
  // it IS the stored time-of-day — sent verbatim as `timeUtc` for preview + save
  // (Interpretation B: the server interprets it in `timezone`). Edit mode seeds it
  // from the stored `timeUtc` directly (no conversion).
  const [localTime, setLocalTime] = useState<string>("09:00");
  const [timezone, setTimezone] = useState<string>(defaultTimezone);
  const [templateId, setTemplateId] = useState<string>("");
  // Story 7.9 — per-schedule open/click tracking opt-in (OFF by default).
  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(false);
  // Story 7.8 — multi-recipient editor state (replaces the single recipientEmail).
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Set on a successful CREATE → swaps the form for the confirmation summary (AC #3).
  const [confirmation, setConfirmation] = useState<ScheduleConfirmation | null>(
    null
  );

  // Reset on OPEN (not close) — resetting on close would flash the form to
  // empty values mid-exit-animation. Pre-fills from `schedule` in edit mode.
  useEffect(() => {
    if (!open) return;
    if (schedule) {
      const tz = schedule.timezone || defaultTimezone;
      setFrequency(schedule.frequency);
      setDayOfWeek(String(schedule.dayOfWeek ?? 1));
      setDayOfMonth(String(schedule.dayOfMonth ?? 1));
      // Seed the picker from the stored time-of-day directly — it is already the
      // local wall-clock time in the schedule's tz (Interpretation B; no conversion).
      setLocalTime(schedule.timeUtc);
      setTimezone(tz);
      setTemplateId(schedule.templateId);
      // Story 7.9 — seed the toggle from the schedule (OFF by default).
      setTrackingEnabled(schedule.trackingEnabled ?? false);
      // Edit-mode recipients load via the recipients useQuery below and seed the
      // editor in a separate effect (a placeholder until they arrive).
      setRecipients([]);
    } else {
      setFrequency("weekly");
      setDayOfWeek("1");
      setDayOfMonth("1");
      setLocalTime("09:00");
      setTimezone(defaultTimezone);
      setTemplateId("");
      // Story 7.9 — tracking OFF by default on create (privacy-first).
      setTrackingEnabled(false);
      // Create mode: seed one primary recipient defaulted to the current user.
      setRecipients([
        { email: currentUserEmail, isPrimary: true },
      ]);
    }
    setErrors({});
    setConfirmation(null);
  }, [open, schedule, defaultTimezone, currentUserEmail]);

  // Lazy-load report templates only while the dialog is open.
  // NOTE: a DISTINCT query key from the report-gallery's useReportTemplates hook
  // (["report-templates"]) — that hook caches a richer ParsedTemplate[] shape
  // (parsedLayout/layoutJson/format), while this picker only needs id+name. Sharing
  // the key would let whichever loads first seed the cache with the wrong shape for
  // the other (5-min staleTime), crashing the gallery's parsedLayout consumers.
  const { data: templates, isLoading: templatesLoading } = useQuery<
    ReportTemplate[]
  >({
    queryKey: ["report-templates", "schedule-picker"],
    enabled: open,
    queryFn: async () => {
      const res = await apiGet(`/api/report-templates`);
      if (!res.ok) {
        const json = (await res.json().catch(() => ({}))) as {
          detail?: string;
          title?: string;
        };
        throw new Error(
          json.detail ?? json.title ?? "Failed to load report templates"
        );
      }
      const json = (await res.json()) as { data: { templates: ReportTemplate[] } };
      return json.data.templates;
    },
  });

  const templateOptions: DialogSelectOption[] = useMemo(
    () =>
      (templates ?? []).map((t) => ({
        value: t.id,
        label: t.name ?? t.title ?? "Untitled template",
      })),
    [templates]
  );

  // Story 7.8 — edit mode loads the schedule's existing recipients (+ suppression
  // state) via GET /api/schedules/:id/recipients. project_react_query_test_provider_gotcha:
  // this useQuery MUST be mocked in any test that transitively renders this dialog.
  const scheduleId = schedule?.id;
  const { data: loadedRecipients } = useQuery({
    queryKey: ["schedule-recipients", scheduleId],
    enabled: open && isEdit && Boolean(scheduleId),
    queryFn: () => getScheduleRecipients(scheduleId!),
  });

  // Seed the editor from the loaded recipients once they arrive (edit mode only).
  useEffect(() => {
    if (!open || !isEdit || !loadedRecipients) return;
    setRecipients(
      normalizeLoadedRecipients(
        loadedRecipients.map((r) => ({
          email: r.email,
          name: r.name ?? undefined,
          isPrimary: r.isPrimary,
          suppressed: r.suppressed,
          suppressionReason: r.suppressionReason,
        }))
      )
    );
  }, [open, isEdit, loadedRecipients]);

  const dayOfWeekNum =
    frequency === "weekly" || frequency === "biweekly"
      ? Number(dayOfWeek)
      : null;
  const dayOfMonthNum = frequency === "monthly" ? Number(dayOfMonth) : null;

  // The picked time IS the stored time-of-day (local wall clock in `timezone`,
  // Interpretation B) — sent verbatim as `timeUtc` on both the preview call and the
  // create/update payload; the server interprets it in the selected zone.
  const timeUtc = localTime;

  // Inline validation mirroring the server Zod (AC #8). Validates the LOCAL pick
  // shape (the derived timeUtc is always well-formed when localTime is).
  const validationErrors = useMemo<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    if (!FREQUENCY_VALUES.includes(frequency)) {
      next.frequency = "Select a frequency";
    }
    if (frequency === "weekly" || frequency === "biweekly") {
      const dow = Number(dayOfWeek);
      if (!Number.isInteger(dow) || dow < 0 || dow > 6) {
        next.dayOfWeek = "Select a day of the week";
      }
    }
    if (frequency === "monthly") {
      const dom = Number(dayOfMonth);
      const valid = dom === LAST_DAY_OF_MONTH || (dom >= 1 && dom <= 28);
      if (!Number.isInteger(dom) || !valid) {
        next.dayOfMonth = "Select a day of the month";
      }
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(localTime)) {
      next.timeUtc = "Select a delivery time";
    }
    if (!timezone) next.timezone = "Select a timezone";
    if (!templateId) next.templateId = "Select a report template";
    // Story 7.8 — recipient-set validation (BOTH modes): max-10, case-insensitive
    // dedupe, >= 1 row, exactly one primary. Mirrors the server refinement.
    const recipientCheck = validateRecipients(recipients);
    Object.assign(next, recipientCheck.errors);
    return next;
  }, [
    frequency,
    dayOfWeek,
    dayOfMonth,
    localTime,
    timezone,
    templateId,
    recipients,
  ]);

  const isValid = Object.keys(validationErrors).length === 0;

  // ── Live preview (Story 7.4, AC #1) — server-computed occurrences ──────────
  // Cadence-only validity (template/recipient are irrelevant to the schedule math)
  // gates the preview query so it fires as soon as the cadence is complete.
  const cadenceValid =
    FREQUENCY_VALUES.includes(frequency) &&
    /^([01]\d|2[0-3]):[0-5]\d$/.test(localTime) &&
    Boolean(timezone) &&
    (frequency === "weekly" || frequency === "biweekly"
      ? dayOfWeekNum !== null &&
        Number.isInteger(dayOfWeekNum) &&
        dayOfWeekNum >= 0 &&
        dayOfWeekNum <= 6
      : true) &&
    (frequency === "monthly"
      ? dayOfMonthNum !== null &&
        Number.isInteger(dayOfMonthNum) &&
        (dayOfMonthNum === LAST_DAY_OF_MONTH ||
          (dayOfMonthNum >= 1 && dayOfMonthNum <= 28))
      : true);

  // Debounce the cadence so rapid picker clicks don't fire a request each time.
  const [debouncedKey, setDebouncedKey] = useState<string>("");
  const cadenceKey = `${frequency}|${dayOfWeekNum ?? ""}|${dayOfMonthNum ?? ""}|${timeUtc}|${timezone}`;
  useEffect(() => {
    if (!open || !cadenceValid) return;
    const t = setTimeout(() => setDebouncedKey(cadenceKey), 300);
    return () => clearTimeout(t);
  }, [open, cadenceValid, cadenceKey]);

  const { data: preview, isFetching: previewFetching } = useQuery({
    queryKey: ["schedule-preview", debouncedKey],
    enabled: open && cadenceValid && debouncedKey === cadenceKey,
    queryFn: () =>
      previewSchedule({
        frequency,
        dayOfWeek: dayOfWeekNum,
        dayOfMonth: dayOfMonthNum,
        timeUtc,
        timezone,
      }),
  });

  const showLastDayNote =
    frequency === "monthly" && dayOfMonthNum === LAST_DAY_OF_MONTH;

  // The auto-detected (or edit-mode) zone may not be in the curated list — always
  // surface the current selection as an option so the picker reflects it.
  const timezoneOptions: DialogSelectOption[] = useMemo(() => {
    if (timezone && !TIMEZONE_VALUES.includes(timezone as never)) {
      return [
        { value: timezone, label: timezone.replace(/_/g, " ") },
        ...TIMEZONE_OPTIONS,
      ];
    }
    return TIMEZONE_OPTIONS;
  }, [timezone]);

  // Map the editor rows → the API recipient payload (replace-set / create set).
  const recipientPayload: ScheduleRecipientInput[] = useMemo(
    () =>
      recipients.map((r) => ({
        email: r.email.trim(),
        name: r.name?.trim() ? r.name.trim() : undefined,
        isPrimary: r.isPrimary,
      })),
    [recipients]
  );

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit && schedule) {
        const input: UpdateScheduleInput = {
          frequency,
          dayOfWeek:
            frequency === "weekly" || frequency === "biweekly"
              ? Number(dayOfWeek)
              : null,
          dayOfMonth: frequency === "monthly" ? Number(dayOfMonth) : null,
          timeUtc,
          timezone,
          templateId,
          // Story 7.9 — persist the tracking toggle.
          trackingEnabled,
          // Story 7.8 — replace-set the recipients on every edit save.
          recipients: recipientPayload,
        };
        return updateSchedule(schedule.id, input).then((s) => ({
          schedule: s,
        }));
      }
      const input: CreateScheduleInput = {
        clientId,
        templateId,
        frequency,
        dayOfWeek:
          frequency === "weekly" || frequency === "biweekly"
            ? Number(dayOfWeek)
            : null,
        dayOfMonth: frequency === "monthly" ? Number(dayOfMonth) : null,
        timeUtc,
        timezone,
        // Story 7.9 — tracking opt-in (OFF by default).
        trackingEnabled,
        recipients: recipientPayload,
      };
      return createSchedule(input);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["schedules", clientId] });
      const sched = result.schedule;
      if (isEdit) {
        showToast(
          "success",
          `Schedule updated — next delivery ${formatNextRun(
            sched.nextRunAt,
            sched.timezone
          )}`
        );
        onOpenChange(false);
        return;
      }
      // Post-save confirmation summary (AC #3): frequency + timing, recipient,
      // template, and "First delivery in X". Surfaced as a rich inline block; the
      // toast carries the short headline.
      const templateName =
        templateOptions.find((o) => o.value === sched.templateId)?.label ??
        "report";
      // Confirmation recipient label: the primary's email + "(+N more)".
      const primary =
        recipients.find((r) => r.isPrimary) ?? recipients[0];
      const others = recipients.length - 1;
      const recipientLabel = primary
        ? others > 0
          ? `${primary.email.trim()} (+${others} more)`
          : primary.email.trim()
        : "";
      setConfirmation({
        frequencyLabel: FREQUENCY_LABELS[sched.frequency],
        timing: cadenceTimingLabel(
          sched.frequency,
          sched.dayOfWeek,
          sched.dayOfMonth,
          localTime,
          sched.timezone
        ),
        recipient: recipientLabel,
        templateName,
        nextRunLabel: formatNextRun(sched.nextRunAt, sched.timezone),
        firstDeliveryIn: formatFirstDeliveryIn(sched.nextRunAt),
      });
      showToast(
        "success",
        `Schedule created — next delivery ${formatNextRun(
          sched.nextRunAt,
          sched.timezone
        )}`
      );
    },
    onError: (
      err: Error & {
        apiError?: { errors?: Array<{ field: string; message: string }> };
      }
    ) => {
      const fieldErrors = err.apiError?.errors;
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        const map: Record<string, string> = {};
        for (const e of fieldErrors) {
          // Server recipient field paths come back as `recipients.<i>.<name|email>`
          // (Zod path joined). Remap them to the editor's `name-<i>`/`email-<i>`
          // keys so the inline error actually RENDERS (otherwise a server-only
          // recipient rejection — e.g. a name the client guard missed — would be a
          // silent stuck form). Unmapped keys fall through verbatim.
          const m = /^recipients\.(\d+)\.(name|email)$/.exec(e.field);
          map[m ? `${m[2]}-${m[1]}` : e.field] = e.message;
        }
        setErrors(map);
      } else {
        showToast("error", err.message ?? "Failed to save schedule");
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }
    mutation.mutate();
  }

  // Block close while submitting (avoids losing in-flight state).
  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  }

  const showDayOfWeek = frequency === "weekly" || frequency === "biweekly";
  const showDayOfMonth = frequency === "monthly";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {confirmation ? (
                  <ScheduleConfirmationView
                    confirmation={confirmation}
                    onDone={() => onOpenChange(false)}
                  />
                ) : (
                <>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-xl bg-muted p-2">
                    <CalendarBlank size={18} className="text-foreground" />
                  </div>
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      {isEdit ? "Edit schedule" : "Schedule report"}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Reports generate and deliver automatically on the cadence
                      you choose.
                    </DialogPrimitive.Description>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                  <FieldShell
                    label="Frequency"
                    htmlFor="schedule-frequency"
                    required
                    error={errors.frequency}
                    delay={0.05}
                  >
                    <DialogSelect
                      id="schedule-frequency"
                      ariaLabel="Frequency"
                      value={frequency}
                      onChange={(v) => {
                        setFrequency(v as Frequency);
                        if (errors.frequency)
                          setErrors((p) => ({ ...p, frequency: "" }));
                      }}
                      options={FREQUENCY_OPTIONS}
                    />
                  </FieldShell>

                  {showDayOfWeek && (
                    <FieldShell
                      label="Day of week"
                      htmlFor="schedule-day-of-week"
                      required
                      error={errors.dayOfWeek}
                      delay={0.1}
                    >
                      <DialogSelect
                        id="schedule-day-of-week"
                        ariaLabel="Day of week"
                        value={dayOfWeek}
                        onChange={(v) => {
                          setDayOfWeek(v);
                          if (errors.dayOfWeek)
                            setErrors((p) => ({ ...p, dayOfWeek: "" }));
                        }}
                        options={DAY_OF_WEEK_OPTIONS}
                      />
                    </FieldShell>
                  )}

                  {showDayOfMonth && (
                    <FieldShell
                      label="Day of month"
                      htmlFor="schedule-day-of-month"
                      required
                      error={errors.dayOfMonth}
                      delay={0.1}
                    >
                      <DialogSelect
                        id="schedule-day-of-month"
                        ariaLabel="Day of month"
                        value={dayOfMonth}
                        onChange={(v) => {
                          setDayOfMonth(v);
                          if (errors.dayOfMonth)
                            setErrors((p) => ({ ...p, dayOfMonth: "" }));
                        }}
                        options={DAY_OF_MONTH_OPTIONS}
                      />
                    </FieldShell>
                  )}

                  <FieldShell
                    label="Time"
                    htmlFor="schedule-time"
                    required
                    error={errors.timeUtc}
                    delay={0.15}
                  >
                    <DialogSelect
                      id="schedule-time"
                      ariaLabel="Delivery time"
                      value={localTime}
                      onChange={(v) => {
                        setLocalTime(v);
                        if (errors.timeUtc)
                          setErrors((p) => ({ ...p, timeUtc: "" }));
                      }}
                      options={TIME_OPTIONS}
                    />
                  </FieldShell>

                  <FieldShell
                    label="Timezone"
                    htmlFor="schedule-timezone"
                    required
                    error={errors.timezone}
                    delay={0.2}
                  >
                    <DialogSelect
                      id="schedule-timezone"
                      ariaLabel="Timezone"
                      value={timezone}
                      onChange={(v) => {
                        setTimezone(v);
                        if (errors.timezone)
                          setErrors((p) => ({ ...p, timezone: "" }));
                      }}
                      options={timezoneOptions}
                      searchPlaceholder="Search timezones…"
                    />
                  </FieldShell>

                  <FieldShell
                    label="Report template"
                    htmlFor="schedule-template"
                    required
                    error={errors.templateId}
                    delay={0.25}
                  >
                    <DialogSelect
                      id="schedule-template"
                      ariaLabel="Report template"
                      value={templateId}
                      onChange={(v) => {
                        setTemplateId(v);
                        if (errors.templateId)
                          setErrors((p) => ({ ...p, templateId: "" }));
                      }}
                      options={templateOptions}
                      placeholder={
                        templatesLoading
                          ? "Loading templates…"
                          : "Select a template"
                      }
                      searchPlaceholder="Search templates…"
                    />
                  </FieldShell>

                  <FieldShell
                    label="Recipients"
                    required
                    error={errors.recipients}
                    delay={0.3}
                  >
                    <RecipientEditor
                      rows={recipients}
                      onChange={(next) => {
                        setRecipients(next);
                        // Clear stale recipient errors on any edit (form-level +
                        // per-row email AND name keys, so a fixed name/email row
                        // doesn't keep showing its old inline error).
                        setErrors((p) => {
                          const cleaned: Record<string, string> = {};
                          for (const [k, v] of Object.entries(p)) {
                            if (
                              k === "recipients" ||
                              k.startsWith("email-") ||
                              k.startsWith("name-")
                            )
                              continue;
                            cleaned[k] = v;
                          }
                          return cleaned;
                        });
                      }}
                      errors={errors}
                      currentUserEmail={currentUserEmail}
                    />
                  </FieldShell>

                  {/* Story 7.9 — open/click tracking toggle (OFF by default). */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.32 }}
                    className="pt-1"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Label
                          htmlFor="tracking-enabled"
                          className="cursor-pointer text-sm font-medium text-foreground"
                        >
                          Track opens &amp; clicks
                        </Label>
                        <p className="mt-0.5 text-[12px] text-muted-foreground">
                          Off by default. When on, the email carries a tracking
                          pixel and click-wrapped links.
                        </p>
                      </div>
                      <Switch
                        id="tracking-enabled"
                        checked={trackingEnabled}
                        onCheckedChange={setTrackingEnabled}
                        className="mt-0.5 shrink-0"
                        data-testid="tracking-enabled-toggle"
                      />
                    </div>
                  </motion.div>

                  {/* Live preview (Story 7.4, AC #1/#4) — server-computed dates. */}
                  <SchedulePreview
                    occurrences={preview ?? null}
                    fetching={previewFetching}
                    cadenceValid={cadenceValid}
                    timezone={timezone}
                    showLastDayNote={showLastDayNote}
                  />

                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                  >
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                      <Button
                        type="button"
                        variant="ghost"
                        className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                        style={{ border: FIELD_BORDER }}
                        onClick={() => onOpenChange(false)}
                        disabled={mutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="brand"
                        className="rounded-xl cursor-pointer"
                        disabled={mutation.isPending || !isValid}
                        data-testid="submit-schedule"
                      >
                        {mutation.isPending
                          ? "Saving…"
                          : isEdit
                            ? "Save changes"
                            : "Create schedule"}
                      </Button>
                    </div>
                  </motion.div>
                </form>
                </>
                )}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

/**
 * Live next-run preview (Story 7.4, AC #1/#4). Renders "Next report: <occ0>" + the
 * next 3 server-computed dates, formatted in the schedule's tz. Handles the
 * incomplete-cadence, loading and empty states, and the monthly "Last day" note.
 */
function SchedulePreview({
  occurrences,
  fetching,
  cadenceValid,
  timezone,
  showLastDayNote,
}: {
  occurrences: number[] | null;
  fetching: boolean;
  cadenceValid: boolean;
  timezone: string;
  showLastDayNote: boolean;
}) {
  const hasData = Boolean(occurrences && occurrences.length > 0);
  return (
    <motion.div
      className="rounded-2xl bg-muted/40 p-4"
      style={{ border: FIELD_BORDER }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.32 }}
      data-testid="schedule-preview"
    >
      <div className="flex items-center gap-2">
        <Clock size={15} className="text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          Delivery preview
        </span>
      </div>

      {!cadenceValid ? (
        <p className="mt-2 text-[13px] text-muted-foreground">
          Choose a frequency, time and timezone to preview delivery dates.
        </p>
      ) : !hasData ? (
        <p
          className="mt-2 text-[13px] text-muted-foreground"
          data-testid="schedule-preview-loading"
        >
          {fetching ? "Calculating delivery dates…" : "No upcoming deliveries."}
        </p>
      ) : (
        <div className="mt-2 space-y-1">
          <p
            className="text-[13px] font-medium text-foreground"
            data-testid="schedule-preview-next"
          >
            Next report: {formatNextRun(occurrences![0]!, timezone)}
          </p>
          {occurrences!.length > 1 && (
            <ul className="space-y-0.5">
              {occurrences!.slice(1, 4).map((ms) => (
                <li
                  key={ms}
                  className="text-[12px] text-muted-foreground/90"
                  data-testid="schedule-preview-date"
                >
                  {formatNextRun(ms, timezone)}
                </li>
              ))}
            </ul>
          )}
          {showLastDayNote && (
            <p
              className="mt-1.5 text-[12px] text-muted-foreground"
              data-testid="schedule-preview-lastday-note"
            >
              Delivers on the last day of each month (Feb → 28/29).
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Post-save confirmation summary (Story 7.4, AC #3). Replaces the form after a
 * successful CREATE with the full summary — frequency + timing, recipient,
 * template, the formatted next run, and "First delivery in X".
 */
function ScheduleConfirmationView({
  confirmation,
  onDone,
}: {
  confirmation: ScheduleConfirmation;
  onDone: () => void;
}) {
  return (
    <div data-testid="schedule-confirmation">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-xl bg-green-500/10 p-2">
          <CalendarBlank size={18} className="text-green-700" />
        </div>
        <div className="flex flex-col space-y-1.5 text-left">
          <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
            Schedule created
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm text-muted-foreground">
            Your report will be generated and delivered automatically.
          </DialogPrimitive.Description>
        </div>
      </div>

      <dl className="mt-6 space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Schedule</dt>
          <dd className="text-right font-medium text-foreground">
            {confirmation.frequencyLabel} — {confirmation.timing}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Report</dt>
          <dd className="text-right font-medium text-foreground">
            {confirmation.templateName}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Recipient</dt>
          <dd className="text-right font-medium text-foreground break-all">
            {confirmation.recipient}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Next delivery</dt>
          <dd className="text-right font-medium text-foreground">
            {confirmation.nextRunLabel}
          </dd>
        </div>
      </dl>

      <p
        className="mt-4 rounded-xl bg-muted/50 px-3 py-2 text-[13px] font-medium text-foreground"
        data-testid="schedule-confirmation-first-delivery"
      >
        {confirmation.firstDeliveryIn}
      </p>

      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          variant="brand"
          className="rounded-xl cursor-pointer"
          onClick={onDone}
          data-testid="schedule-confirmation-done"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
